import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { Contact, getTagArrayFromString } from 'src/classes/contact';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, Validators, FormGroup, FormControl } from '@angular/forms';
import { ContactService } from 'src/services/contact/contact.service';
import generateRandomPassword from 'src/utils/generateRandomPassword';

@Component({
  selector: 'app-contact-form-modal',
  templateUrl: './contact-form-modal.component.html',
  styleUrls: ['./contact-form-modal.component.scss']
})
export class ContactFormModalComponent implements OnInit {

  @Input() public contact?: Contact;
  @Input() public initialError?: string;
  @Input() public availableTags?: [];

  public form: FormGroup;
  public lastPassword?: string;
  public errors: string[] = [];
  public forceShowError: boolean;
  public sendingEmailWithPassword: boolean;
  public sentPdfPassword: string;
  public tagArray: string[] = [];
  @ViewChild('tagInput', {static: true}) tagInput: ElementRef;

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private contactService: ContactService
  ) { }

  ngOnInit() {
    this.form = this.formBuilder.group({
      idContact: [this.contact ? this.contact.idContact : '0'],
      name: [this.contact ? this.contact.name : '', [Validators.required, Validators.min(4)]],
      title: [this.contact ? this.contact.title : '', []],
      email: [this.contact ? this.contact.email : '', []],
      phone: [this.contact ? this.contact.phone : '', []],
      company: [this.contact ? this.contact.company : '', null],
      department: [this.contact ? this.contact.department : '', []],
      tags: [this.contact ? getTagArrayFromString(this.contact.tags) : [], []],
      pdfpassword: [this.contact ? this.contact.pdfpassword : '', this.contact && this.contact.pdfpassword ? Validators.required : null],
      isPasswordEnabled: [this.contact && this.contact.pdfpassword ? true : false]
    });

    if (this.initialError) {
      this.errors = this.initialError.split('\n');
      this.forceShowError = true;
    }

    this.updateTagList();
  }

  get inputs() {
    return this.form.controls;
  }

  updateTagList() {
    const control = this.form.get('tags') as FormControl;
    const valueString = (control.value || '');
    if (!valueString) {
      // Empty tag
      this.tagArray = [];
      return;
    }
    this.tagArray = getTagArrayFromString(valueString);
  }

  populateErrors() {
    if (this.form.get('name').errors) {
      this.errors.push('The name is required');
    }
    if (this.form.get('title').errors) {
      this.errors.push('The title is required');
    }
    if (this.form.get('department').errors) {
      this.errors.push('The department is required');
    }
    if (this.form.get('email').errors) {
      this.errors.push('The email must be valid');
    }
    if (this.form.get('phone').errors) {
      this.errors.push('The phone is required');
    }
    if (this.form.get('company').errors) {
      this.errors.push('The company is required');
    }
    if (this.form.get('pdfpassword').errors) {
      this.errors.push('The pdf password is marked as active and therefore it is required');
    }
    if (this.errors.length === 0) {
      this.errors.push('The form is invalid');
    }
  }

  async onSendEmailWithPasswordClick() {
    if (this.sentPdfPassword || this.sendingEmailWithPassword) {
      this.sentPdfPassword = null;
      return;
    }
    this.sendingEmailWithPassword = true;
    try {
      await new Promise(r => setTimeout(r, 200));
      const targetEmail = this.form.get('email').value;
      const pdfPassword = this.form.get('pdfpassword').value;
      if (true === await this.contactService.sendPdfPassword(targetEmail, pdfPassword)) {
        this.sentPdfPassword = pdfPassword;
      }
    } catch(err) {
      this.errors = err.message.split('\n');
    }
    this.sendingEmailWithPassword = false;
  }

  onSubmitClick() {
    this.forceShowError = false;
    this.errors = [];
    if (this.form.invalid) {
      this.populateErrors();
      return;
    }
    const contact = this.contact || new Contact();
    contact.idContact = parseInt(this.form.get('idContact').value, 10);
    if (isNaN(contact.idContact)) {
      contact.idContact = undefined;
    }
    contact.name = this.form.get('name').value;
    contact.title = this.form.get('title').value;
    contact.department = this.form.get('department').value;

    const tagValue = this.form.get('tags').value;
    if (tagValue instanceof Array && tagValue.length === 0) {
      contact.tags = "";
    } else if (tagValue instanceof Array) {
      let tagArray = [];
      for (let tag of tagValue) {
        if (typeof tag === "object") {
          if (typeof tag.value !== "string") {
            throw new Error("Unknown type for tag object value");
          }
          tagArray.push(tag.value);
        } else if (typeof tag === "string") {
          tagArray.push(tag);
        } else {
          throw new Error("Unknown type for tag array entry");
        }
      }
      contact.tags = tagArray.join(" ");
    } else {
      throw new Error("Unknown type for input value");
    }
    contact.email = this.form.get('email').value;
    contact.phone = this.form.get('phone').value;
    contact.company = this.form.get('company').value;
    contact.pdfpassword = this.isEnabledPassword ? this.form.get('pdfpassword').value : '';
    this.activeModal.close(contact);
  }

  onCancelClick() {
    this.activeModal.dismiss(0);
  }

  get isEnabledPassword() {
    return this.form.get('isPasswordEnabled').value;
  }

  get isNew() {
    return !(this.contact && this.contact.idContact);
  }

  onPasswordSwitch(enabled: boolean) {
    const pdfPasswordControl = this.form.get('pdfpassword') as FormControl;
    if (this.lastPassword) {
      pdfPasswordControl.setValue(this.lastPassword);
    }
    const control = this.form.get('isPasswordEnabled') as FormControl;
    control.setValue(enabled);
    control.markAsTouched();
    control.markAsDirty();
    if (enabled) {
      pdfPasswordControl.setValidators([Validators.required]);
      pdfPasswordControl.setErrors(pdfPasswordControl.value ? null : {required: true});
      pdfPasswordControl.markAsTouched();
      pdfPasswordControl.markAsDirty();
    } else {
      pdfPasswordControl.setValidators(null);
      const passwordControl = this.form.get('pdfpassword') as FormControl;
      this.lastPassword = passwordControl.value as string;
      passwordControl.setValue('');
      passwordControl.markAsDirty();
      passwordControl.markAsTouched();
    }
  }

  onGeneratePasswordClick() {
    const passwordEnabled = this.form.get('isPasswordEnabled') as FormControl;
    passwordEnabled.setValue(true);
    passwordEnabled.markAsTouched();
    passwordEnabled.markAsDirty();
    const control = this.form.get('pdfpassword') as FormControl;
    control.setValue(generateRandomPassword());
    control.markAsTouched();
    control.markAsDirty();
  }

}
